import React, { FC, useEffect, useMemo } from "react";
import cn from "classnames";
import { useMediaQuery } from "react-responsive";
import { useDebounce } from "use-debounce";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { useQueryParams } from "src/hooks/useQueryParams";
import { selectorIsLoadingPosts, selectorPosts } from "src/redux/blog/selector";
import { getBlogPostsAsync, ITEMS_PER_PAGE } from "src/redux/blog/actions";
import { BlogCard } from "src/components/BlogCard";
import { Loader } from "src/components/Loader";
import { Pagination } from "src/components/Pagination";
import { scrollTop } from "src/utils/scrollTop";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import { Sizes } from "src/@types/sizes";
import { Categories } from "./Categories";
import { NoSearchResults } from "../NoSearchResults";
import { QueryParamKeys } from "../types";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";

const DEBOUNCE_DELAY_DESKTOP = 1000;
const DEBOUNCE_DELAY_MOBILE = 2500;

export const MainSection: FC = () => {
  const isGLScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.GL,
  });
  const debounceDelay = isGLScreenBreakpoint
    ? DEBOUNCE_DELAY_MOBILE
    : DEBOUNCE_DELAY_DESKTOP;

  const dispatch = useAppDispatch();

  const { queryParams, getQueryParam, setQueryParam } = useQueryParams();

  const searchQuery = getQueryParam(QueryParamKeys.SEARCH) || "";

  const [debouncedSearchQuery] = useDebounce(searchQuery, debounceDelay);

  const filteredQueryParams = useMemo(() => {
    const params = new URLSearchParams(queryParams);

    if (debouncedSearchQuery) {
      params.set(QueryParamKeys.SEARCH, debouncedSearchQuery);
    } else {
      params.delete(QueryParamKeys.SEARCH);
    }

    return params.toString();
  }, [debouncedSearchQuery, queryParams]);

  const posts = useAppSelector(selectorPosts);
  const isLoading = useAppSelector(selectorIsLoadingPosts);

  useEffect(() => {
    dispatch(
      getBlogPostsAsync({
        filters: filteredQueryParams,
      })
    );
  }, [dispatch, filteredQueryParams]);

  const postsResults = posts?.results || [];

  const currentPage = Number(getQueryParam(QUERY_PARAM_KEYS.PAGE)) || 1;

  const setPage = (page: number) => {
    scrollTop();
    if (page !== currentPage) {
      setQueryParam(QUERY_PARAM_KEYS.PAGE, page.toString());
    }
  };

  const pageCount = Math.ceil(posts?.count / ITEMS_PER_PAGE);
  const isPaginated = pageCount > 1;

  return (
    <div className="flex flex-col-reverse xl:flex-row items-start gap-5 gl:gap-10">
      {isLoading ? (
        <div className="flex justify-center items-center w-full xl:min-w-200 min-h-80">
          <Loader size={Sizes.XXL} />
        </div>
      ) : posts?.count > 0 ? (
        <div className="flex flex-col gap-10 w-full">
          <div
            className={cn("grid grid-cols-1 gap-5 min-w-full xl:min-w-200", {
              "sm:grid-cols-2 md:grid-cols-1": postsResults?.length > 1,
            })}
          >
            {postsResults.map((post) => (
              <BlogCard key={post.id} {...post} />
            ))}
          </div>

          {isPaginated && (
            <Pagination
              page={currentPage}
              pageCount={pageCount}
              setPage={setPage}
            />
          )}
        </div>
      ) : (
        <NoSearchResults />
      )}

      <Categories />
    </div>
  );
};
